import React from 'react';
import { View, Text, StyleSheet, StyleProp, ViewStyle } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { PaprikaDietWash } from '../../styles/theme';
import { classifyDietKind, DietKind } from '../../utils/dietClassifier';
import { recipeInitial } from '../../utils/recipeInitial';

/** What the classifier needs; both recipe index shapes already satisfy it. */
interface PlaceholderRecipe {
  title?: string | null;
  ingredients?: string[];
  diets?: string[];
}

interface RecipePlaceholderProps {
  recipe: PlaceholderRecipe;
  /**
   * The box's smaller dimension, in points. Drives the monogram only — the box
   * itself is sized and shaped by `style`.
   */
  size: number;
  /** Shape and layout: width/height/borderRadius/flex. */
  style?: StyleProp<ViewStyle>;
  /** Small diet marker, top-left. Only worth it where the box can carry it. */
  showDietDot?: boolean;
  testID?: string;
}

/**
 * What a recipe surface draws when it has no usable photo — missing URL or
 * failed load, which `RemoteImage` folds into the same `fallback`.
 *
 * A pale diet-tinted wash with the dish's initial in Newsreader italic. The
 * typographic treatment is the point: it is the app's own signature move, so a
 * photo-less card reads as composed rather than as a missing asset, and two
 * photo-less recipes still look like different recipes.
 *
 * This replaces three separate emoji placeholders (#669). Emoji were both a
 * DESIGN.md violation and a lie — they came from English-substring ingredient
 * ladders that fall through to a generic glyph on the French corpus.
 *
 * The diet tint comes from `classifyDietKind`, the canonical FR/EN classifier —
 * never from a local keyword ladder. The tint is a soft signal by design: it is
 * pale enough that a misclassification is not a claim the user can read as
 * wrong, unlike a fish icon on a chicken dish.
 */
export const RecipePlaceholder: React.FC<RecipePlaceholderProps> = ({
  recipe,
  size,
  style,
  showDietDot = false,
  testID,
}) => {
  const diet: DietKind = classifyDietKind({
    title: recipe.title ?? '',
    ingredients: recipe.ingredients ?? [],
    diets: recipe.diets,
  });
  const tone = PaprikaDietWash[diet];
  const initial = recipeInitial(recipe.title);

  return (
    <LinearGradient
      colors={[tone.washStart, tone.washEnd]}
      start={{ x: 0, y: 0 }}
      end={{ x: 1, y: 1 }}
      style={[styles.container, style]}
      testID={testID}
    >
      {initial ? (
        <Text
          style={[styles.monogram, { fontSize: Math.round(size * 0.48), color: tone.ink }]}
          // The wash already says "no photo"; the letter is decoration on top of
          // the title, which every one of these surfaces renders in full.
          accessibilityElementsHidden
          importantForAccessibility="no-hide-descendants"
          allowFontScaling={false}
        >
          {initial}
        </Text>
      ) : null}

      {showDietDot ? <View style={[styles.dot, { backgroundColor: tone.dot }]} /> : null}
    </LinearGradient>
  );
};

const styles = StyleSheet.create({
  container: {
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
  },
  monogram: {
    fontFamily: 'Newsreader-Italic',
    // Newsreader's italic cap-height sits high in the line box; without this the
    // letter reads as top-aligned inside a circle.
    includeFontPadding: false,
    textAlign: 'center',
  },
  dot: {
    position: 'absolute',
    top: 8,
    left: 8,
    width: 7,
    height: 7,
    borderRadius: 3.5,
  },
});

export default RecipePlaceholder;
